import { experimentsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { CandidateIdType } from '../types/create-experiment.types';
import type { DraftExperiment, DraftExperimentUpdateBody } from '../types/experiment-draft.types';

/**
 * Create a draft experiment by committing the user's grouping-method choice
 * + selected ad platform. Both `parentIdField` (the `matchBy` code) and
 * `adPlatform` (e.g. `GOOGLE_ADS`, `META_ADS`) are sent per the backend
 * contract; later wizard steps PATCH this draft's `id` as more fields are
 * collected.
 *
 * When an `armAssignmentFile` is supplied (Restart flow), the request must be
 * `multipart/form-data`: the CSV plus the two scoping fields ride in the
 * `FormData` body. We deliberately do NOT set a `Content-Type` header — the
 * browser derives the multipart boundary from the `FormData`, and hand-setting
 * it is the single most common way this integration breaks. Without a file, the
 * original empty-body + query-param contract is preserved so the normal wizard
 * create path is unchanged.
 */
export async function createExperimentDraft(args: {
  parentIdField: string;
  adPlatform: string;
  armAssignmentFile?: File;
  /** Candidate-scope CSV picked on Define Scope, before any draft existed to
   *  attach it to. Rides the create as `offerIdsFile`. */
  offerIdsFile?: File;
  /** Which identifier `offerIdsFile` holds. Required whenever the file is sent. */
  offerIdsType?: CandidateIdType;
}): Promise<DraftExperiment> {
  if (args.armAssignmentFile || args.offerIdsFile) {
    const form = new FormData();
    form.append('parentIdField', args.parentIdField);
    form.append('adPlatform', args.adPlatform);
    if (args.armAssignmentFile) form.append('armAssignmentFile', args.armAssignmentFile);
    if (args.offerIdsFile) {
      form.append(OFFER_IDS_FILE_PART, args.offerIdsFile, args.offerIdsFile.name);
      form.append(OFFER_IDS_TYPE_PART, args.offerIdsType ?? 'OFFER_ID');
    }
    return experimentsClient
      .post(endpoints.experiments.create(), { body: form })
      .json<DraftExperiment>();
  }
  return experimentsClient
    .post(endpoints.experiments.create(), {
      searchParams: { parentIdField: args.parentIdField, adPlatform: args.adPlatform },
    })
    .json<DraftExperiment>();
}

/** Multipart part name for the candidate-scope CSV. Fixed by the backend
 *  contract — see `docs/experiment-candidate-scope.md`. */
const OFFER_IDS_FILE_PART = 'offerIdsFile';

/**
 * Tells the backend which identifier the CSV holds (`OFFER_ID` /
 * `ITEM_GROUP_ID`); omitted means `OFFER_ID`. The part name and the path both
 * say "offer ids" for either type — those names predate item-group support and
 * are kept for compatibility, so `idType` is what decides the meaning.
 */
const OFFER_IDS_TYPE_PART = 'idType';

/**
 * POST (multipart) `experiments/{id}/offer-ids` — sets or replaces the candidate
 * scope with a CSV of merchant offer ids.
 *
 * The FILE is sent rather than the parsed ids: an operator's list can run to
 * hundreds of thousands of ids, which is more than a request body should carry,
 * and the backend parses and stores it to GCS anyway. Upload REPLACES any
 * existing scope — a re-upload is the operator correcting the list.
 *
 * As with `createExperimentDraft`, no `Content-Type` header is set: the browser
 * derives the multipart boundary from the `FormData`, and setting it by hand is
 * the single most common way this breaks.
 *
 * Validates the file's SHAPE only. Whether the ids match real products is
 * decided at content scoring, which is when `offerIdsMatched` populates.
 */
export async function uploadExperimentOfferIds(
  id: string,
  file: File,
  idType: CandidateIdType,
): Promise<DraftExperiment> {
  const form = new FormData();
  form.append(OFFER_IDS_FILE_PART, file, file.name);
  form.append(OFFER_IDS_TYPE_PART, idType);
  return experimentsClient
    .post(endpoints.experiments.offerIds(id), { body: form })
    .json<DraftExperiment>();
}

/**
 * DELETE `experiments/{id}/offer-ids` — clears the candidate scope, returning
 * the experiment to filter-only selection. Use this rather than uploading an
 * empty file, which is a 400.
 */
export async function deleteExperimentOfferIds(id: string): Promise<DraftExperiment> {
  return experimentsClient.delete(endpoints.experiments.offerIds(id)).json<DraftExperiment>();
}

/**
 * Fetch a draft experiment by id for the resume flow. Returns the FULL wire
 * DTO without any list-page stripping — `mapDtoToExperiment` (in
 * experiment.service.ts) drops fields the wizard needs (KPIs, content
 * attributes, filters, channel), so we hit the same endpoint with a
 * dedicated fetcher and let the hydrator consume the raw shape.
 */
export async function fetchExperimentDraft(id: string): Promise<DraftExperiment> {
  return experimentsClient.get(endpoints.experiments.byId(id)).json<DraftExperiment>();
}

/**
 * Discard a draft from the listing page. Backend deletes the row outright;
 * caller invalidates the experiments list cache on success so the row
 * disappears from the table.
 */
export async function deleteExperimentDraft(id: string): Promise<void> {
  await experimentsClient.delete(endpoints.experiments.delete(id));
}

/**
 * Multipart part names for the arm-assignment-enabled draft PATCH.
 * TODO(BE): align with the final `@RequestPart` names the backend settles on —
 * the draft-update endpoint is gaining multipart support so the Experiment
 * Strategy step can attach an arm-assignment CSV. Only these two consts change
 * if the contract differs.
 */
const DRAFT_BODY_PART = 'request';
const ARM_FILE_PART = 'armAssignmentFile';

/**
 * PATCH the draft with whatever fields the calling wizard step is committing
 * (e.g. `scopingFilters` + `stage` on the Define Scope → Product Grouping
 * transition). Filters are sent as-is — no serialization at this boundary.
 * Response is the bare updated draft.
 *
 * When an `armAssignmentFile` is supplied (Experiment Strategy step), the
 * request is `multipart/form-data`: the draft fields ride as a JSON part
 * alongside the CSV. We deliberately do NOT set `Content-Type` — the browser
 * derives the multipart boundary from the `FormData`, and hand-setting it is the
 * single most common way this integration breaks. Without a file the original
 * JSON PATCH is used unchanged, so every other wizard step is untouched.
 */
export async function updateExperimentDraft(
  draftId: string,
  body: DraftExperimentUpdateBody,
  armAssignmentFile?: File,
): Promise<DraftExperiment> {
  if (armAssignmentFile) {
    const form = new FormData();
    form.append(DRAFT_BODY_PART, new Blob([JSON.stringify(body)], { type: 'application/json' }));
    form.append(ARM_FILE_PART, armAssignmentFile);
    return experimentsClient
      .patch(endpoints.experiments.update(draftId), { body: form })
      .json<DraftExperiment>();
  }
  return experimentsClient
    .patch(endpoints.experiments.update(draftId), { json: body })
    .json<DraftExperiment>();
}
